import {makeAutoObservable, observable} from 'mobx';

import {indexOfId} from '@/domain';
import {Reaction} from '@/domain/models/Reaction';
import queryClient from '@/shared/lib/react-query/queryClient';

import {FEATURES_QUERY_SENT} from '../consts/queryKeys';
import type {LastViewport} from './types';

class SentStore {
  list = observable.array<Reaction>([], {deep: false});
  count = 0;
  fetchedAllReactions: boolean = false;
  private _shouldRefresh: boolean = false;
  private _lastViewport: LastViewport | undefined;

  constructor() {
    makeAutoObservable(this, {
      lastViewport: false,
    });
  }

  setLastViewport(value: LastViewport) {
    this._lastViewport = value;
  }

  clearLastViewport() {
    this._lastViewport = undefined;
  }

  get lastViewport() {
    return this._lastViewport;
  }

  get shouldRefresh() {
    return this._shouldRefresh;
  }

  setShouldRefresh(shouldRefresh: boolean) {
    this._shouldRefresh = shouldRefresh;
  }

  private get reactionsIds() {
    return new Set(this.list.map((item) => item.id));
  }

  get lastLoadedReactionAt() {
    return this.list[this.list.length - 1]?.created_at;
  }

  get empty() {
    return this.list.length === 0 && this.fetchedAllReactions;
  }

  setFetchedAllReactions(fetched: boolean) {
    this.fetchedAllReactions = fetched;
  }

  addReactions(list: Reaction[]) {
    if (list.length === 0) {
      return;
    }

    const nextItems = list.filter((item) => !this.reactionsIds.has(item.id));

    if (nextItems.length > 0) {
      this.list.push(...nextItems);
    }
  }

  setCount(count: number) {
    this.count = count;
  }

  addOrReplaceReaction(reaction: Reaction) {
    const index = this.list.findIndex((item) => item.id === reaction.id);

    if (index >= 0) {
      this.list[index] = reaction;
      return;
    }

    this.list.unshift(reaction);
    this.count += 1;
  }

  applyLatestReactions(list: Reaction[]): boolean {
    const topReaction = this.list[0];

    if (topReaction) {
      const topReactionIndex = indexOfId(topReaction.id, list);

      if (topReactionIndex >= 0) {
        this.list.unshift(...list.slice(0, topReactionIndex));
        return true;
      }
    }

    this.list.replace(list);
    this.fetchedAllReactions = false;
    this.clearLastViewport();

    return false;
  }

  removeByUser(userId: number) {
    const nextList = this.list.filter(
      (reaction) => reaction.user_id !== userId
    );
    const removedCount = this.list.length - nextList.length;

    if (removedCount === 0) {
      return;
    }

    this.list.replace(nextList);
    this.count = Math.max(0, this.count - removedCount);
  }

  resetList() {
    this.list.clear();
    this.fetchedAllReactions = false;
    this.dropCache();
    this.clearLastViewport();
  }

  dropCache() {
    queryClient.removeQueries({
      queryKey: [FEATURES_QUERY_SENT],
    });
  }
}

export const sentStore = new SentStore();
